import store from '../store';
import {isFunction, isObject, isString} from 'lodash';

/**
 * @see https://gist.github.com/ivalkenburg/3e73b98a0c9b1c8dae71bd1c76363360
 * @see https://css-tricks.com/protecting-vue-routes-with-navigation-guards/
 * @param routes
 * @returns {*}
 */
function guestGuard(routes) {
    return routes.map(route => {
        return {
            ...route,
            beforeEnter: (to, from, next) => {
                if (store.getters.isAdmin) { // MLD-2719
                    window.location.assign('/admin/presentations');
                    next(false);
                } else if (store.getters.isMember) {
                    next({name: 'library.home'})
                } else {
                    next()
                }
            }
        };
    });
}

/**
 * @see https://gist.github.com/ivalkenburg/3e73b98a0c9b1c8dae71bd1c76363360
 * @see https://css-tricks.com/protecting-vue-routes-with-navigation-guards/
 * @param routes
 * @returns {*}
 */
function authGuard(routes) {
    return routes.map(route => {
        return {
            ...route,
            beforeEnter: async (to, from, next) => {
                if (store.getters.isAuthenticated === false) {
                    await store.dispatch('fetchUser');
                }
                if (store.getters.user && store.getters.team) {
                    if (store.getters.team.restricted_users_enabled && store.getters.user.is_restricted_user) { // MLD-2750
                        if (!isObject(to.meta) || (isObject(to.meta) && !to.meta.allow_restricted_user)) {
                            window.app?.$toast.error('You cannot go there.');
                            next(from?.name ? {name: from.name} : new Error('You cannot go there.'));
                            return
                        }
                    }
                    if (isObject(to.meta) && isString(to.meta.permission)) { // MLD-5518
                        if (store.getters.hasPermission(to.meta.permission) === false) {
                            window.app?.$toast.error('You cannot go there.');
                            next(from?.name ? {name: from.name} : new Error('You cannot go there.'));
                            return;
                        }
                    }
                    if (isObject(to.meta) && isFunction(to.meta.gate)) { // MLD-2882
                        if (to.meta.gate() === false) {
                            window.app?.$toast.error('You cannot go there.');
                            next(from?.name ? {name: from.name} : new Error('You cannot go there.'));
                            return;
                        }
                    }
                    next();
                } else {
                    console.log('authGuard fetchUser failed, sending to login');
                    next({name: 'login', query: {redirect: to.fullPath}})
                }
            }
        }
    });
}

function adminGuard(routes) {
    return routes.map(route => {
        return {
            ...route,
            beforeEnter: async (to, from, next) => {
                if (store.getters.isMember) { // Members are not allowed here
                    next({name: 'library.home'});
                } else if (store.getters.isAdmin) {
                    if (isObject(to.meta) && isString(to.meta.permission)) {
                        if (store.getters.hasPermission(to.meta.permission) === false) {
                            window.app?.$toast.error('You cannot go there.');
                            next(from?.name ? {name: from.name} : new Error('You cannot go there.'));
                            return;
                        }
                    }
                    if (isObject(to.meta) && isFunction(to.meta.gate)) { // MLD-2882
                        if (to.meta.gate() === false) {
                            window.app?.$toast.error('You cannot go there.');
                            next(from?.name ? {name: from.name} : new Error('You cannot go there.'));
                            return;
                        }
                    }
                    next();
                } else {
                    console.log('We should never get here!!!');
                    await store.dispatch('fetchUser');

                    if (store.getters.isMember) { // Members are not allowed here
                        next({name: 'library.home'});
                    } else if (store.getters.isAdmin) {
                        if (isObject(to.meta) && isString(to.meta.permission)) {
                            if (store.getters.hasPermission(to.meta.permission) === false) {
                                window.app?.$toast.error('You cannot go there.');
                                next(from?.name ? {name: from.name} : new Error('You cannot go there.'));
                                return;
                            }
                        }
                        next();
                    } else {
                        await store.dispatch('logout');

                        console.log('adminGuard fetchUser failed, sending to login');
                        next({name: 'login', query: {redirect: to.fullPath}})
                    }
                }
            },
        };
    });
}

function spaceGuard(routes) { // MLD-5030
    return routes.map(route => {
        return {
            ...route,
            beforeEnter: async (to, from, next) => {
                if (store.getters.isMember) { // Members are not allowed here
                    next({name: 'library.home'});
                } else if (store.getters.isAdmin && store.getters.canUserCreateSpaces) {
                    next();
                } else {
                    next(from?.name ? {name: from.name} : new Error('You cannot go there.'));
                }
            },
        };
    });
}

function employeeGuard(routes) {
    return routes.map(route => {
        return {
            ...route,
            beforeEnter: async (to, from, next) => {
                if (store.getters.isEmployee) {
                    next();
                } else {
                    await store.dispatch('fetchUser');

                    const user = store.getters.user;
                    if (user && user.is_employee) {
                        next();
                    } else {
                        await store.dispatch('logout');

                        console.log('employeeGuard fetchUser failed, sending to login');
                        next({name: 'login', query: {redirect: to.fullPath}})
                    }
                }
            }
        };
    });
}

function teamGuard(teamID, routes) {
    return routes.map(route => {
        return {
            ...route,
            beforeEnter: async (to, from, next) => {
                const team = store.getters.team;
                if (team && team.id === teamID) {
                    next();
                } else {
                    next(from?.name ? {name: from.name} : new Error('You cannot go there.'));
                }
            }
        };
    });
}

export {
    adminGuard,
    authGuard,
    employeeGuard,
    guestGuard,
    spaceGuard,
    teamGuard,
};
